import * as turf from "@turf/turf"
import booleanValid from "@turf/boolean-valid"
import { wktToGeoJSON, geojsonToWKT } from "@terraformer/wkt"
import { Point } from "../types"

export function reverseCoordinates(inputString: string) {
  if (!inputString) {
    return
  }
  // Parse the input string into an array of coordinate pairs
  let coordinates = JSON.parse(inputString)

  // Iterate through each coordinate pair and reverse the order of elements
  for (let i = 0; i < coordinates.length; i++) {
    coordinates[i] = coordinates[i].reverse().map((val: number) => parseFloat(val.toFixed(5)))
  }

  // Convert the array back to a string
  let reversedString = JSON.stringify(coordinates)

  return reversedString
}

export function roundCoordinates(inputString: string) {
  if (!inputString) {
    return
  }
  // Parse the input string into a 2D array of coordinates
  let coordinates = JSON.parse(inputString)

  // Iterate over each coordinate pair and round the numbers to the specified decimal places
  let roundedCoordinates = coordinates.map((pair: any[]) =>
    pair.map((coord) => {
      return parseFloat(Number(coord).toFixed(3))
    })
  )

  // Convert the rounded coordinates back to a string
  let roundedString = JSON.stringify(roundedCoordinates)

  return roundedString
}

export function isValidJSON(formData: string) {
  if (!formData) {
    return
  }
  try {
    JSON.parse(formData)
    return true
  } catch (error) {
    return false
  }
}

export function isValidPolygon(formData: string) {
  if (!formData) {
    return
  }
  try {
    const coordinates = JSON.parse(formData)

    // Create a Turf.js Feature object with the coordinates
    const polygonFeature = turf.polygon([coordinates])
    const isValidPolygon = booleanValid(polygonFeature)
    return isValidPolygon
  } catch (error) {
    return false
  }
}

export function isValidCoordinateString(formData: string) {
  if (!formData) {
    return
  }
  try {
    const coordinates = JSON.parse(formData)
    if (!Array.isArray(coordinates)) return false
    for (const pair of coordinates) {
      if (!Array.isArray(pair) || pair.length !== 2) return false
      const [x, y] = pair
      if (typeof x !== "number" || typeof y !== "number" || isNaN(x) || isNaN(y)) return false
    }
    return true
  } catch (error) {
    return false
  }
}

export function convertJSONToWKT(inputString: string) {
  if (!inputString) {
    return
  }
  let coordinates = JSON.parse(inputString)
  var polygon = geojsonToWKT({
    type: "Polygon",
    coordinates: [coordinates]
  })
  return polygon
}

export function convertWktToJSON(inputString: string) {
  if (!inputString) {
    return
  }
  const geojson = wktToGeoJSON(inputString) as any
  const coordinates = JSON.stringify(geojson.coordinates[0])
  return coordinates
}

export function isValidPolygonWKT(wktInput: string) {
  if (!wktInput) {
    return false
  }
  try {
    // Attempt to parse the WKT input text using the Terraformer library
    const geojson = wktToGeoJSON(wktInput)

    // If the parsing is successful and the type is Polygon, return true
    if (
      geojson.type === "Polygon" &&
      validateCoordinates(geojson.coordinates) &&
      isPolygonClosed(geojson.coordinates)
    ) {
      return true
    } else {
      return false
    }
  } catch (error) {
    // If an error occurs during parsing, the input is not a valid WKT
    return false
  }
}

export function dropInteriorRings(wktInput: string) {
  if (!wktInput) {
    return
  }
  try {
    // Parse the WKT input text using the Terraformer library
    const geojson = wktToGeoJSON(wktInput)

    // Check if the parsed GeoJSON is a Polygon
    if (geojson.type === "Polygon") {
      // Extract only the exterior ring coordinates
      const exteriorRing = geojson.coordinates[0]

      // Convert the new GeoJSON object back to WKT
      const newWkt = geojsonToWKT({
        type: "Polygon",
        coordinates: [exteriorRing as any]
      })
      return newWkt
    } else {
      return false
    }
  } catch (error) {
    // If any error occurs, the input is not a valid WKT
    return false
  }
}

function validateCoordinates(coordinates: any[]) {
  return coordinates.every((ring) =>
    ring.every(
      (point: any[]) => Array.isArray(point) && point.length === 2 && point.every((num) => typeof num === "number")
    )
  )
}

function isPolygonClosed(coordinates: any[]) {
  return coordinates.every((ring) => {
    const firstPoint = ring[0]
    const lastPoint = ring[ring.length - 1]
    return firstPoint[0] === lastPoint[0] && firstPoint[1] === lastPoint[1]
  })
}

export function areasMatch(boundaryCoordinates: string | undefined, tcpsArea: Point[]) {
  try {
    const boundaryCoordinatesArray = boundaryCoordinates ? JSON.parse(boundaryCoordinates) : []
    if (boundaryCoordinatesArray.length !== tcpsArea?.length) {
      return false
    }

    const precision = 0.001
    for (let i = 0; i < tcpsArea.length; i++) {
      if (
        Math.abs(boundaryCoordinatesArray[i][0] - tcpsArea[i].latitude) > precision ||
        Math.abs(boundaryCoordinatesArray[i][1] - tcpsArea[i].longitude) > precision
      ) {
        return false
      }
    }
    return true
  } catch (e) {
    return false
  }
}

export function crsNamesAreEqual(crsName: string | undefined, tcpsCrsName: string | undefined) {
  const a = (crsName || "").trim()
  const b = (tcpsCrsName || "").trim()
  return a === b
}
